import React, { Dispatch, SetStateAction, useState } from "react"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputLedyerConfig } from "lib/types/generated/graphql-types"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { PROVIDER_CATEGORY, PROVIDER_TYPE } from "lib/types/common"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import DeleteButton from "components/Ui/EditSidebar/DeleteButton"
import ConnectedStoreMarket from "components/Config/ConnectedStoreMarkets/ConnectedStoreMarket"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"

import { ReactComponent as Logo } from "images/providers/ledyer-logo.svg"
import { handleErrorMessages } from "helpers/errors"
import { GET_LEDYER_CONFIG } from "../../../../graphql/queries/config/Providers/Ledyer/GetLedyerConfig"
import { CREATE_OR_UPDATE_LEDYER_CONFIG } from "../../../../graphql/mutations/config/Providers/Ledyer/CreateOrUpdateLedyerConfig"
import { VALIDATE_LEDYER } from "../../../../graphql/queries/config/Providers/Ledyer/ValidateLedyerConfig"
import { DELETE_LEDYER_CONFIG } from "../../../../graphql/mutations/config/Providers/Ledyer/DeleteLedyerConfig"
import { LedyerConfigInputs } from "../../../../components/Config/Ledyer/LedyerConfigInputs"
import { WithAuth } from "../../../../components/Auth"
import { PermissionGroups } from "../../../../routes/constants"

type Props = {
  refetch: () => void
  editId: string
  setEditId: Dispatch<SetStateAction<string>>
}

interface ExtendedInputLedyerConfig {
  id: string
  ledyerConfig: InputLedyerConfig
}

export const EditLedyerConfig = ({ refetch, editId, setEditId }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const { data } = useQuery(GET_LEDYER_CONFIG, {
    variables: { id: editId }
  })
  const closeSidebar = () => {
    setEditId("")
    dispatch(hideEditSidebar())
  }

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<ExtendedInputLedyerConfig>({ mode: "onBlur" })

  const [updateLedyerConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_LEDYER_CONFIG,
    {
      onCompleted: () => {
        dispatch(hideEditSidebar())
        reset(ledyerConfig)
        dispatch(
          alertActions.actions.createAlert({
            message: "ledyer configuration successfully updated",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )
  const ledyerConfig = data?.getLedyerConfig
  const { id } = ledyerConfig

  const [validateLedyer, { loading }] = useLazyQuery(VALIDATE_LEDYER, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputLedyerConfig> = (data) => {
    const { baseUrl, clientId, clientSecret, authUrl } = data.ledyerConfig
    validateLedyer({
      variables: {
        baseUrl,
        authUrl,
        clientId,
        clientSecret
      }
    }).then((response) => {
      if (response.data.Configuration.validate.ledyer.valid) {
        updateLedyerConfig({
          variables: {
            id: editId,
            ledyerConfig: {
              baseUrl,
              authUrl,
              clientId,
              clientSecret
            }
          }
        })
      } else {
        setValidationError(true)
      }
    })
  }

  const directSubmit: SubmitHandler<ExtendedInputLedyerConfig> = (data) => {
    const { baseUrl, authUrl, clientId, clientSecret } = data.ledyerConfig

    updateLedyerConfig({
      variables: {
        id: editId,
        ledyerConfig: {
          baseUrl,
          authUrl,
          clientId,
          clientSecret
        }
      }
    })
    setValidationError(false)
  }

  if (!ledyerConfig) return null

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar cancelEvent={() => closeSidebar()}>
        <EditSidebarHeader icon={<Logo />} title={id} id={id} cancelEvent={() => closeSidebar()}>
          <WithAuth requiredGroups={PermissionGroups.providers.write}>
            <PrimaryButton type="submit" loading={createLoading || loading}>
              Save
            </PrimaryButton>
            <DeleteButton
              id={id}
              name={id}
              entity={"ledyer configuration"}
              redirectUrl="/settings/providers/ledyer"
              mutation={DELETE_LEDYER_CONFIG}
              refetch={refetch}
              postDeleteFunction={() => setEditId("")}
              secureDelete={true}
            />
          </WithAuth>

        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with ledyer. Do you want to save the ledyer config anyway?"
            buttonText="Yes, save"
            handleOkClick={handleSubmit(directSubmit)}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Connected store markets</Tab>
          </TabList>
          <TabPanel>
            <LedyerConfigInputs
              control={control}
              errors={errors}
              setValue={setValue}
              defaultValues={ledyerConfig}
              showId={false}
            />
          </TabPanel>
          <TabPanel>
            <ConnectedStoreMarket
              providerId={ledyerConfig.id}
              providerType={PROVIDER_TYPE.LEDYER}
              providerCategory={PROVIDER_CATEGORY.PAYMENT}
            />
          </TabPanel>
        </Tabs>
      </EditSidebar>
    </form>
  )
}
